import React from 'react';
import {
  Button,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  ToolbarFilterMenu,
  type FilterToolbarChip,
  type MultiSelectOption,
} from '@chatsdet/ui';
import { VISUAL_CHECKPOINT_STATUSES } from '@chatsdet/types';
import {
  VISUAL_SOURCES,
  parseVisualSource,
  isVisualSearchFiltered,
  parseVisualStatus,
  type VisualSearch,
} from '../../../features/visual/visualSearch';

const STATUS_LABELS: Record<VisualSearch['status'], string> = {
  changed: 'Changed',
  healed: 'Healed',
  new: 'New',
  unstable: 'Unstable',
  all: 'All',
};
const STATUS_OPTIONS: MultiSelectOption[] = VISUAL_CHECKPOINT_STATUSES.map((id) => ({ id, label: STATUS_LABELS[id] }));
const SOURCE_OPTIONS: MultiSelectOption[] = VISUAL_SOURCES.map((id) => ({
  id,
  label: id === 'native' ? 'Cofactor flows' : 'Playwright',
}));

type Props = {
  search: VisualSearch;
  onChange: (updates: Partial<VisualSearch>) => void;
  onClear: () => void;
  suiteOptions: MultiSelectOption[];
  workflowOptions: MultiSelectOption[];
  specOptions: MultiSelectOption[];
  environmentOptions: MultiSelectOption[];
};

const one = (id: string | undefined) => (id ? [id] : []);

export function VisualFilterBar({
  search,
  onChange,
  onClear,
  suiteOptions,
  workflowOptions,
  specOptions,
  environmentOptions,
}: Props) {
  const chips: FilterToolbarChip[] = [];
  if (search.execution) chips.push({ id: 'execution', label: 'Test run' });
  if (search.run) chips.push({ id: 'run', label: 'External run' });
  const hasFilters = isVisualSearchFiltered(search);

  return (
    <FilterToolbar>
      <ToolbarFilterMenu
        label="Status"
        options={STATUS_OPTIONS}
        selectedIds={[search.status]}
        selectionMode="single"
        searchable={false}
        // Picking the selected status again clears it, which falls back to the default.
        onChange={(ids) => onChange({ status: parseVisualStatus(ids[0]) })}
      />
      <ToolbarFilterMenu
        label="Suite"
        options={suiteOptions}
        selectedIds={one(search.suite)}
        selectionMode="single"
        onChange={(ids) => onChange({ suite: ids[0] })}
      />
      <ToolbarFilterMenu
        label="Flow"
        options={workflowOptions}
        selectedIds={one(search.workflow)}
        selectionMode="single"
        onChange={(ids) => onChange({ workflow: ids[0] })}
      />
      <ToolbarFilterMenu
        label="Spec"
        options={specOptions}
        selectedIds={one(search.spec)}
        selectionMode="single"
        onChange={(ids) => onChange({ spec: ids[0] })}
      />
      <ToolbarFilterMenu
        label="Environment"
        options={environmentOptions}
        selectedIds={one(search.environment)}
        selectionMode="single"
        onChange={(ids) => onChange({ environment: ids[0] })}
      />
      <ToolbarFilterMenu
        label="Source"
        options={SOURCE_OPTIONS}
        selectedIds={one(search.source)}
        selectionMode="single"
        searchable={false}
        onChange={(ids) => onChange({ source: parseVisualSource(ids[0]) })}
      />
      <FilterToolbarDivider />
      {hasFilters && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onPress={onClear}>
          Clear
        </Button>
      )}
      <FilterToolbarChips
        chips={chips}
        onRemove={(id) => onChange(id === 'execution' ? { execution: undefined } : { run: undefined })}
      />
    </FilterToolbar>
  );
}
